import React from 'react';
import { Shield, Clock, AlertCircle } from 'lucide-react';

interface WarrantyBadgeProps {
  /** Warranty expiry date - can be "CONTRATUAL" (string), Date object/string, or null */
  warrantyExpiryDate?: string | Date | null;
  /** Optional size variant */
  size?: 'sm' | 'md';
}

/**
 * Badge component to display warranty status with visual indicators
 * Supports:
 * - "CONTRATUAL" -> Lifetime/Contractual warranty (green, shield icon)
 * - Valid date -> Warranty valid (green, clock icon)
 * - Expired date -> Warranty expired (red, alert icon)
 * - No date -> Unknown warranty status (gray, question icon)
 */
export const WarrantyBadge: React.FC<WarrantyBadgeProps> = ({
  warrantyExpiryDate,
  size = 'md'
}) => {
  // No warranty information
  if (!warrantyExpiryDate) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 ${size === 'sm' ? 'text-[10px]' : 'text-xs'}`}>
        <AlertCircle className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        Sem informação
      </span>
    );
  }

  // Check if it's CONTRATUAL (lifetime warranty)
  if (warrantyExpiryDate === 'CONTRATUAL') {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 ${size === 'sm' ? 'text-[10px]' : 'text-xs'}`}>
        <Shield className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        Vitalícia/Contratual
      </span>
    );
  }

  // Parse date string to Date object if needed
  let expiryDate: Date;
  if (typeof warrantyExpiryDate === 'string') {
    expiryDate = new Date(warrantyExpiryDate);
  } else {
    expiryDate = warrantyExpiryDate;
  }

  // Invalid date
  if (isNaN(expiryDate.getTime())) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 ${size === 'sm' ? 'text-[10px]' : 'text-xs'}`}>
        <AlertCircle className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        Data inválida
      </span>
    );
  }

  // Calculate days until expiry
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryNormalized = new Date(expiryDate);
  expiryNormalized.setHours(0, 0, 0, 0);

  const diffTime = expiryNormalized.getTime() - today.getTime();
  const daysUntilExpiry = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Format date for display (dd/mm/yyyy)
  const formattedDate = expiryDate.toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  // Expired warranty
  if (daysUntilExpiry < 0) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200 ${size === 'sm' ? 'text-[10px]' : 'text-xs'}`}
        title={`Garantia expirada em ${formattedDate}`}
      >
        <AlertCircle className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        Expirada
      </span>
    );
  }

  // Expiring soon (less than 30 days)
  if (daysUntilExpiry <= 30) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 ${size === 'sm' ? 'text-[10px]' : 'text-xs'}`}
        title={`Garantia expira em ${formattedDate}`}
      >
        <Clock className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        {daysUntilExpiry}d restantes
      </span>
    );
  }

  // Valid warranty
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 ${size === 'sm' ? 'text-[10px]' : 'text-xs'}`}
      title={`Garantia válida até ${formattedDate}`}
    >
      <Clock className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      Válida
    </span>
  );
};

export default WarrantyBadge;
